import {
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { StateService } from '@uirouter/core';
import { ToastService } from '../../../../ng1-factories';

/**
 * VHAT V3 wrapper component.
 *
 * Loads the VHAT React app from /vhat/ (same-origin) inside an iframe.
 * Source code lives in src/app/client/venue-hours-angular/vhat-v3/ and
 * is built by `npm run build:vhat` (Vite) into src/assets/vhat/.
 *
 * Authentication:
 *   - Same-origin cookies provide baseline session access
 *   - CR session token is explicitly passed via postMessage after iframe
 *     loads, so VHAT can validate and use it for API calls
 *
 * Context (customerId, venueId, environment) is passed via URL query
 * params. VHAT reads these on boot to skip its login screen.
 */
@Component({
    selector: 'cr-venue-hours-v3',
    templateUrl: './venue-hours-v3.component.html',
    styleUrls: ['./venue-hours-v3.component.scss'],
})
export class VenueHoursV3Component implements OnInit, OnDestroy {
    @ViewChild('vhatFrame', { static: false }) vhatFrame: ElementRef<HTMLIFrameElement>;

    iframeSrc: SafeResourceUrl;
    isLoading = true;

    private messageHandler: (event: MessageEvent) => void;

    constructor(
        private sanitizer: DomSanitizer,
        private state: StateService,
        private crToastService: ToastService,
    ) {}

    ngOnInit(): void {
        const params = new URLSearchParams({
            customerId: this.state.params.customerId || '',
            venueId: this.state.params.venueId || '',
            env: this.detectEnvironment(),
            region: this.detectRegion(),
            embedded: 'true',
        });

        // Forward ?dev=true from parent URL so developers can enable dry-run mode
        if (this.state.params.dev === 'true'
            || new URLSearchParams(window.location.search).get('dev') === 'true'
            || window.location.hash.includes('dev=true')) {
            params.set('dev', 'true');
        }

        const iframeUrl = `/vhat/index.html?${params.toString()}`;
        this.iframeSrc = this.sanitizer.bypassSecurityTrustResourceUrl(iframeUrl);

        this.messageHandler = (event: MessageEvent) => this.handleMessage(event);
        window.addEventListener('message', this.messageHandler);
    }

    ngOnDestroy(): void {
        if (this.messageHandler) {
            window.removeEventListener('message', this.messageHandler);
        }
    }

    onIframeLoad(): void {
        this.isLoading = false;
        this.sendSessionToIframe();
    }

    /**
     * Send session context to the VHAT iframe via postMessage.
     * This provides explicit authentication proof beyond same-origin cookies.
     * VHAT uses this to confirm the session is valid before making API calls.
     */
    private sendSessionToIframe(): void {
        const iframe = this.vhatFrame?.nativeElement;
        if (!iframe?.contentWindow) {
            return;
        }
        iframe.contentWindow.postMessage(
            {
                type: 'vhat-session',
                customerId: this.state.params.customerId || '',
                venueId: this.state.params.venueId || '',
                env: this.detectEnvironment(),
                region: this.detectRegion(),
            },
            window.location.origin,
        );
    }

    private detectEnvironment(): string {
        const hostname = window.location.hostname;
        if (hostname.includes('.stage.')) {
            return 'stage';
        }
        if (hostname.includes('control-room.te2.io')) {
            return 'prod';
        }
        // Local dev — default to stage since webpack devServer proxies to a real env
        return 'stage';
    }

    private detectRegion(): string {
        const hostname = window.location.hostname;
        if (hostname.includes('.ap.')) {
            return 'ap';
        }
        return 'us';
    }

    private handleMessage(event: MessageEvent): void {
        if (event.origin !== window.location.origin) {
            return;
        }

        const data = event.data;
        if (!data || !data.type) {
            return;
        }

        switch (data.type) {
            case 'vhat-resize':
                // Iframe fills viewport via CSS — no dynamic height needed
                break;

            case 'vhat-toast':
                if (data.msg) {
                    this.crToastService.toast(data.msg, data.timeToLive || 3000);
                }
                break;

            case 'vhat-ready':
                this.isLoading = false;
                // Re-send session in case iframe loaded before React mounted
                this.sendSessionToIframe();
                break;

            case 'vhat-navigate':
                if (data.route) {
                    this.state.go(data.route, data.params || {});
                }
                break;
        }
    }
}
